// src/components/Patients/PatientDetailsDrawer.jsx
import React, { useEffect, useState } from "react";
import { getPatientById } from "../../Services/patientService";

export const PatientDetailsDrawer = ({
  patientId,
  patientUhid,
  onClose,
  onOpenOpdDetails,
}) => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const targetId = patientId || patientUhid;

  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      if (!targetId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getPatientById(targetId);
        if (isMounted) {
          setPatient(data);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Failed to load patient details:", err);
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load patient record.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [targetId]);

  if (!targetId) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(dateString);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 24, 39, 0.5)",
        backdropFilter: "blur(2px)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 1060,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "640px",
          height: "100vh",
          backgroundColor: "#ffffff",
          boxShadow: "-4px 0 25px rgba(0, 0, 0, 0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h4
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: "18px",
                color: "#111827",
              }}
            >
              {loading ? "Loading Patient..." : patient?.name || patient?.fullName || "Patient Record"}
            </h4>
            <div
              style={{
                fontSize: "13px",
                color: "#6B7280",
                marginTop: "2px",
                display: "flex",
                gap: "12px",
              }}
            >
              <span>
                <strong>UHID:</strong>{" "}
                <span style={{ fontFamily: "monospace" }}>
                  {patient?.uhid || "—"}
                </span>
              </span>
              {patient?.opdId && (
                <span>
                  <strong>OPD ID:</strong>{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {patient.opdId}
                  </span>
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              lineHeight: 1,
              color: "#9CA3AF",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "6px",
            }}
          >
            &times;
          </button>
        </div>

        {/* Drawer Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 0",
                color: "#6B7280",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2.5rem", height: "2.5rem" }}
              />
              <p style={{ marginTop: "16px", fontSize: "14px" }}>
                Loading full patient record...
              </p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
              <div style={{ marginTop: "8px" }}>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    getPatientById(targetId)
                      .then((d) => setPatient(d))
                      .catch((e) => setError(e.message))
                      .finally(() => setLoading(false));
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : patient ? (
            <>
              {/* Profile Card */}
              <div
                style={{
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "14px",
                    fontSize: "13px",
                  }}
                >
                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      MOBILE NUMBER
                    </span>
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>
                      {patient.mobile || "—"}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      GENDER / AGE
                    </span>
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>
                      {patient.gender || "—"} / {patient.age ? `${patient.age} yr` : "—"}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      BLOOD GROUP
                    </span>
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>
                      {patient.bloodGroup || "—"}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      ABHA ID
                    </span>
                    <span style={{ fontWeight: 600, color: "#1E293B", fontFamily: "monospace" }}>
                      {patient.maskedAbha ||
                        (patient.isAbhaLinked
                          ? "Linked"
                          : "Not Linked")}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      MASKED AADHAAR
                    </span>
                    <span style={{ fontWeight: 600, color: "#1E293B", fontFamily: "monospace" }}>
                      {patient.maskedAadhaar || patient.aadhaar_masked || "—"}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                      TOTAL VISITS
                    </span>
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#1429BB",
                        fontSize: "14px",
                      }}
                    >
                      {patient.totalVisits ?? patient.visits?.length ?? 0}
                    </span>
                  </div>

                  {patient.abhaAddress && (
                    <div style={{ gridColumn: "span 3" }}>
                      <span style={{ color: "#64748B", fontSize: "11px", display: "block" }}>
                        ABHA ADDRESS
                      </span>
                      <span style={{ fontWeight: 600, color: "#1E293B", fontFamily: "monospace" }}>
                        {patient.abhaAddress}
                      </span>
                    </div>
                  )}
                </div>

                {/* Address & Email */}
                <div
                  style={{
                    borderTop: "1px solid #E2E8F0",
                    marginTop: "14px",
                    paddingTop: "12px",
                    fontSize: "12px",
                    color: "#475569",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div>
                    <strong>Address:</strong>{" "}
                    {patient.address || "N/A"}{" "}
                    {patient.pincode ? `(${patient.pincode})` : ""}
                  </div>
                  <div>
                    <strong>Email:</strong> {patient.email || "N/A"}
                  </div>
                  {patient.emergencyContact && (
                    <div>
                      <strong>Emergency Contact:</strong> {patient.emergencyContact}
                    </div>
                  )}
                  {Array.isArray(patient.insurance) && patient.insurance.length > 0 && (
                    <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", marginTop: "2px" }}>
                      <strong>Insurance:</strong>{" "}
                      {patient.insurance.map((ins, idx) => (
                        <span key={idx} className="badge bg-light text-dark border">
                          {ins.provider || "Insurance"}: {ins.policyNumber}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Historical Visits (Chronologically Sorted: Newest First) */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#1E293B",
                    }}
                  >
                    Visit History
                  </h5>
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#64748B",
                      backgroundColor: "#F1F5F9",
                      padding: "3px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    Chronologically Sorted (Newest First)
                  </span>
                </div>

                {patient.visits && patient.visits.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {patient.visits.map((v, index) => (
                      <div
                        key={v.visitId || v._id || index}
                        style={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E2E8F0",
                          borderRadius: "10px",
                          padding: "14px",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                          }}
                        >
                          <div>
                            <span
                              className="badge"
                              style={{
                                backgroundColor:
                                  v.visitType === "IPD"
                                    ? "#FEF3C7"
                                    : v.visitType === "Emergency"
                                    ? "#FEE2E2"
                                    : "#DBEAFE",
                                color:
                                  v.visitType === "IPD"
                                    ? "#92400E"
                                    : v.visitType === "Emergency"
                                    ? "#991B1B"
                                    : "#1E40AF",
                                fontWeight: 600,
                                fontSize: "11px",
                                marginRight: "8px",
                              }}
                            >
                              {v.visitType || "General OPD"}
                            </span>
                            <strong style={{ fontSize: "13px", color: "#0F172A" }}>
                              {v.doctorName ? `Dr. ${v.doctorName}` : "General Physician"}
                            </strong>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#64748B",
                                marginLeft: "6px",
                              }}
                            >
                              ({v.department || "General"})
                            </span>
                          </div>
                          <span style={{ fontSize: "12px", color: "#64748B" }}>
                            {formatDate(v.visitDate || v.createdAt)}
                          </span>
                        </div>

                        <div
                          style={{
                            borderTop: "1px solid #F1F5F9",
                            paddingTop: "8px",
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "8px",
                            fontSize: "12px",
                            color: "#475569",
                          }}
                        >
                          <div>
                            <span style={{ color: "#94A3B8" }}>OPD ID:</span>{" "}
                            <span style={{ fontWeight: 500, fontFamily: "monospace" }}>
                              {v.opdId || "—"}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: "#94A3B8" }}>Queue Token:</span>{" "}
                            <span style={{ fontWeight: 600, color: "#0F172A" }}>
                              {v.tokenNumber || "—"}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: "#94A3B8" }}>Fee:</span>{" "}
                            <span style={{ fontWeight: 500, color: "#0F172A" }}>
                              {v.consultationFee !== undefined && v.consultationFee !== ""
                                ? `₹${v.consultationFee}`
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {v.description && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#64748B",
                              backgroundColor: "#F8FAFC",
                              padding: "6px 10px",
                              borderRadius: "6px",
                            }}
                          >
                            <strong>Notes:</strong> {v.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      border: "1px dashed #CBD5E1",
                      borderRadius: "10px",
                      textAlign: "center",
                      color: "#94A3B8",
                      fontSize: "13px",
                    }}
                  >
                    No previous visit records found.
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Drawer Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {onOpenOpdDetails && patient && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => onOpenOpdDetails(patient)}
            >
              Open Full OPD Consultation
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm ms-auto"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsDrawer;
